import React, { useEffect, useCallback, useMemo, useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Typography, Box, Menu, MenuItem, MuiThemeProvider } from '@material-ui/core'
import { css } from '@emotion/css'
import PopoverButton from 'components/shared/PopoverButton'
import ConfirmationDialog from 'components/shared/ConfirmationDialog'
import ConfirmationDialogGeneric from 'components/shared/ConfirmationDialogGeneric'
import { materialAtlasTheme } from 'material-atlas/theme'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import LinearIndeterminate from 'components/shared/LinearProgress'
import { ColorBaseDarkRed, ColorBaseSkyBlue } from 'assets/styles/variables'
import i18n from 'common/util/internationalization'
import authenticationManager from 'businesslayer/authenticationManager'
import emitter from 'common/util/events'
import {
    PageLayout,
    PageBody,
    PageHeader,
    MinutesManagerToolbar
} from 'components/layout/page-layout'
import { AtlasButton } from 'components/atlas-material/button'
import { AtlasIcon } from '@diligentcorp/atlas-react'
import { useWritingStylesStyles } from './writing-styles.styles'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import DiligentBrandIcon from 'assets/icons/DiligentBrandIcon'
import {
    useWritingStyles,
    WritingStyle
} from 'businesslayer/networkrequests/writingstyles/useWritingStyles'
import DefaultStarIcon from 'assets/icons/DefaultStarIcon'
import AILabelNewIcon from 'assets/icons/AILabelNewIcon'
import AIGeneratedNewIcon from 'assets/icons/AIGeneratedNewIcon'
import StatusToast from 'components/atlas-components/atlas-toast/Toast'
import {
    generateWritingStyles,
    updateWritingStylePreference,
    deleteCustomAIStyle
} from 'businesslayer/networkrequests/AIStylesService'
import { useMinutesPolling } from 'common/customHooks/pollingApiStatus'
import { WRITING_STYLE_STATUS } from 'components/minutetaker/components/InvitationView/types'
import { POLLING_INTERVAL } from 'common/constants/constants'
import { useCommitteesState } from 'components/contexts/committee-context'
import RegenerateIcon from 'assets/icons/RegenerateIcon'
import TrashIconRed from 'assets/icons/TrashIconRed'
declare global {
    interface Window {
        canAddMinutes: boolean
    }
}

// Menu option value constants
const MENU_OPTION_EDIT = 'EDIT'
const MENU_OPTION_REGENERATE = 'REGENERATE'
const MENU_OPTION_PREFERRED = 'PREFERRED'
const MENU_OPTION_DELETE = 'DELETE'

// Style ID constants
const HARDCODED_DEFAULT_STYLE_ID = '0'

interface WritingStyleDocument {
    id: string | number
    attributes: {
        writing_style_status: string
    }
}

export const WritingStylesPage = () => {
    const classes = useWritingStylesStyles()
    const history = useHistory()

    const {
        writingStyles,
        setWritingStyles,
        isLoading,
        setIsLoading,
        fetchWritingStyles
    } = useWritingStyles()

    const [AIstylesgenerationstatus, setAIstylesgenerationstatus] = useState<string>('')
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false)
    const [styleToDelete, setStyleToDelete] = useState<WritingStyle | null>(null)
    const [showRegenerateConfirmation, setShowRegenerateConfirmation] = useState<boolean>(false)
    const [styleToRegenerate, setStyleToRegenerate] = useState<string | null>(null)
    const [deletingStyleId, setDeletingStyleId] = useState<string | null>(null)
    const { adminCommittees } = useCommitteesState()

    const prevAIStatusRef = useRef<string>('')

    const {
        writingStyleDocuments,
        isPolling,
        setIsPolling,
        fetchMinutesDocuments: fetchPollingMinutesDocuments,
        toasts,
        setToasts
    } = useMinutesPolling()

    const committeeIds = useMemo(() => {
        return adminCommittees
            .filter((committee) => committee.id && committee.id.trim() !== '')
            .map((committee) => Number(committee.id))
            .filter((id) => !isNaN(id) && id > 0)
    }, [adminCommittees])

    useEffect(() => {
        fetchWritingStyles()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        // Emit canAddMinutes event so navbar shows all options
        // User must have admin rights to access this page
        emitter.emit('canAddMinutes')
    }, [isPolling])

    // Polling for writing style status updates
    useEffect(() => {
        let intervalId: number | undefined
        if (isPolling) {
            fetchPollingMinutesDocuments()
            intervalId = setInterval(fetchPollingMinutesDocuments, POLLING_INTERVAL) as number
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPolling])

    // Update AIstylesgenerationstatus based on writingStyleDocuments from polling
    useEffect(() => {
        if (writingStyleDocuments && writingStyleDocuments.length > 0) {
            const latestWritingStyle = writingStyleDocuments[0] as WritingStyleDocument
            if (latestWritingStyle?.attributes?.writing_style_status) {
                const newStatus = latestWritingStyle.attributes.writing_style_status
                setAIstylesgenerationstatus(newStatus)
            }
        }
    }, [writingStyleDocuments])

    useEffect(() => {
        const prevStatus = prevAIStatusRef.current
        const currentStatus = AIstylesgenerationstatus

        if (
            prevStatus === WRITING_STYLE_STATUS.PROGRESS &&
            currentStatus !== WRITING_STYLE_STATUS.PROGRESS &&
            currentStatus !== ''
        ) {
            fetchWritingStyles()
        }

        prevAIStatusRef.current = currentStatus
    }, [AIstylesgenerationstatus, fetchWritingStyles])

    const getFirstCustomStyle = useCallback((): WritingStyle | undefined => {
        return writingStyles.find((style) => style.id !== HARDCODED_DEFAULT_STYLE_ID)
    }, [writingStyles])

    const isHardcodedDefault = useCallback((styleId: string): boolean => {
        return styleId === HARDCODED_DEFAULT_STYLE_ID
    }, [])

    const updateStyleAttributes = useCallback(
        (style: WritingStyle, updates: Partial<WritingStyle['attributes']>): WritingStyle => {
            return {
                ...style,
                attributes: {
                    ...style.attributes,
                    name: style.attributes?.name || style.title,
                    ...updates
                }
            }
        },
        []
    )

    const handlePreferredUpdate = useCallback(
        async (selectedStyle: WritingStyle) => {
            const firstCustomStyle = getFirstCustomStyle()

            if (!firstCustomStyle) {
                // No custom styles: only update local state
                setWritingStyles((prev) =>
                    prev.map((style) =>
                        updateStyleAttributes(style, {
                            preferred: style.id === selectedStyle.id
                        })
                    )
                )
                return
            }

            const isDefaultSelected = isHardcodedDefault(selectedStyle.id)
            const apiStyleId = isDefaultSelected ? firstCustomStyle.id : selectedStyle.id
            const apiPreferredValue = !isDefaultSelected

            await updateWritingStylePreference(apiStyleId, apiPreferredValue)

            setWritingStyles((prev) =>
                prev.map((style) => {
                    const isDefault = isHardcodedDefault(style.id)
                    const preferred = isDefault
                        ? isDefaultSelected
                        : style.id === selectedStyle.id && !isDefaultSelected

                    return updateStyleAttributes(style, { preferred })
                })
            )
        },
        [getFirstCustomStyle, isHardcodedDefault, updateStyleAttributes, setWritingStyles]
    )

    const handleRegenerate = useCallback((styleId: string) => {
        setStyleToRegenerate(styleId)
        setShowRegenerateConfirmation(true)
    }, [])

    const handleRegenerateConfirm = useCallback(async () => {
        if (styleToRegenerate) {
            try {
                setAIstylesgenerationstatus(WRITING_STYLE_STATUS.PROGRESS)
                const result = await generateWritingStyles(committeeIds, styleToRegenerate)
                if (result?.data?.[0]?.attributes?.writing_style_status) {
                    const status = result.data[0].attributes.writing_style_status
                    setAIstylesgenerationstatus(status)
                    if (status === WRITING_STYLE_STATUS.PROGRESS) {
                        setIsPolling(true)
                    } else {
                        //Polling api trigger for failed case
                        await fetchPollingMinutesDocuments(true)
                    }
                }

                await fetchWritingStyles()
            } catch (error) {
                setAIstylesgenerationstatus(WRITING_STYLE_STATUS.FAILED)
                console.error('Error regenerating style:', error)
            } finally {
                setShowRegenerateConfirmation(false)
                setStyleToRegenerate(null)
            }
        }
    }, [
        styleToRegenerate,
        committeeIds,
        fetchWritingStyles,
        setIsPolling,
        fetchPollingMinutesDocuments
    ])

    const handleDelete = useCallback((writingStyle: WritingStyle) => {
        setStyleToDelete(writingStyle)
        setShowDeleteConfirmation(true)
    }, [])

    const handleDeleteConfirm = useCallback(async () => {
        if (styleToDelete) {
            try {
                setIsLoading(true)
                setDeletingStyleId(styleToDelete.id)
                await deleteCustomAIStyle(styleToDelete.id)
                await fetchWritingStyles()
                emitter.emit('writing-style:deleted', { styleId: styleToDelete.id })
            } catch (error) {
                setIsLoading(false)
                console.error('Error deleting style:', error)
            } finally {
                setIsLoading(false)
                setDeletingStyleId(null)
                setShowDeleteConfirmation(false)
                setStyleToDelete(null)
            }
        }
    }, [styleToDelete, fetchWritingStyles, setIsLoading])

    const handleGenerateStyle = useCallback(async () => {
        setIsSubmitting(true)
        try {
            const writingStylesGenerationResult = await generateWritingStyles(committeeIds)
            if (writingStylesGenerationResult?.data?.[0]?.attributes?.writing_style_status) {
                const status = writingStylesGenerationResult.data[0].attributes.writing_style_status
                setAIstylesgenerationstatus(status)
                if (status === WRITING_STYLE_STATUS.PROGRESS) {
                    setIsPolling(true)
                } else {
                    // Polling api trigger for failed case
                    await fetchPollingMinutesDocuments(true)
                }
            }
        } catch (error) {
            setAIstylesgenerationstatus(WRITING_STYLE_STATUS.FAILED)
            console.error('Error generating style:', error)
        } finally {
            setIsSubmitting(false)
        }
    }, [committeeIds, setIsPolling, fetchPollingMinutesDocuments])

    const hasCustomStyle = useMemo(() => {
        return writingStyles.some((style) => style.id !== HARDCODED_DEFAULT_STYLE_ID)
    }, [writingStyles])

    useEffect(() => {
        if (AIstylesgenerationstatus === WRITING_STYLE_STATUS.SUCCESS && hasCustomStyle) {
            setAIstylesgenerationstatus('')
        }
    }, [AIstylesgenerationstatus, hasCustomStyle])

    const navigateToStyleDetail = useCallback(
        (writingStyle: WritingStyle) => {
            if (writingStyle.id === HARDCODED_DEFAULT_STYLE_ID) return
            if (deletingStyleId === writingStyle.id) return
            history.push(`/writing-style-library/details/${writingStyle.id}`)
        },
        [history, deletingStyleId]
    )

    const handleMenuOptionClick = useCallback(
        async (
            option: any,
            writingStyle: WritingStyle,
            onClosePopover: (optionValue?: any) => void
        ) => {
            onClosePopover(option.value)
            try {
                if (option.value === MENU_OPTION_EDIT) {
                    navigateToStyleDetail(writingStyle)
                } else if (option.value === MENU_OPTION_PREFERRED) {
                    await handlePreferredUpdate(writingStyle)
                } else if (option.value === MENU_OPTION_REGENERATE) {
                    handleRegenerate(writingStyle.id)
                } else if (option.value === MENU_OPTION_DELETE) {
                    handleDelete(writingStyle)
                }
            } catch (error) {
                console.error('Error handling menu option:', error?.message || error)
            }
        },
        [navigateToStyleDetail, handlePreferredUpdate, handleRegenerate, handleDelete]
    )

    const getAnalyticsValue = useCallback((option: any, writingStyle: WritingStyle):
        | string
        | undefined => {
        if (option.dataAnalytics === 'MM-Minute-Styles-Preferred') {
            return writingStyle.id !== HARDCODED_DEFAULT_STYLE_ID
                ? 'MM-Minute-Styles-Preferred'
                : undefined
        }
        return option.dataAnalytics
    }, [])

    const removeToast = useCallback(
        (toastId: number | string) => {
            setToasts((prev) => prev.filter((t) => t.id !== toastId))
        },
        [setToasts]
    )

    const renderMenuItem = useCallback(
        (option: any, writingStyle: WritingStyle, onClosePopover: (optionValue?: any) => void) => {
            // Hide "Edit" and "Re-generate" options for default style
            if (
                writingStyle.id === HARDCODED_DEFAULT_STYLE_ID &&
                (option.value === MENU_OPTION_EDIT || option.value === MENU_OPTION_REGENERATE)
            ) {
                return null
            }

            const isDisabled =
                (option.value === MENU_OPTION_REGENERATE &&
                    AIstylesgenerationstatus === WRITING_STYLE_STATUS.PROGRESS) ||
                (option.value === MENU_OPTION_PREFERRED &&
                    AIstylesgenerationstatus === WRITING_STYLE_STATUS.PROGRESS) ||
                (option.value === MENU_OPTION_DELETE &&
                    writingStyle.id === HARDCODED_DEFAULT_STYLE_ID) ||
                (writingStyle.id !== HARDCODED_DEFAULT_STYLE_ID &&
                    AIstylesgenerationstatus === WRITING_STYLE_STATUS.PROGRESS)

            return (
                <MenuItem
                    key={option.id}
                    disabled={isDisabled}
                    style={{
                        height: '55px'
                    }}
                    className={css`
                        &:focus {
                            border: 2px solid ${ColorBaseSkyBlue};
                            border-radius: 2px;
                        }
                        &:hover {
                            background-color: ${isDisabled ? 'transparent' : '#ebeef2'};
                            border-radius: 2px;
                        }
                        ${isDisabled ? 'opacity: 0.5; cursor: not-allowed;' : ''}
                    `}
                    data-analytics={getAnalyticsValue(option, writingStyle)}
                    data-testid={option.testid}
                    onClick={() => {
                        if (!isDisabled) {
                            handleMenuOptionClick(option, writingStyle, onClosePopover)
                        }
                    }}>
                    <div className={classes.menuOptionsAlignment}>
                        <div className={classes.menuItemIcon}>{option.icon}</div>
                        <div
                            style={{
                                color:
                                    option.value === MENU_OPTION_DELETE
                                        ? ColorBaseDarkRed
                                        : 'inherit'
                            }}>
                            {option.label}
                        </div>
                    </div>
                </MenuItem>
            )
        },
        [
            AIstylesgenerationstatus,
            classes.menuOptionsAlignment,
            classes.menuItemIcon,
            getAnalyticsValue,
            handleMenuOptionClick
        ]
    )

    const MENU_OPTIONS = useMemo(
        () => [
            {
                id: 0,
                value: MENU_OPTION_EDIT,
                testid: 'MinutesCardOverflow_Edit',
                dataAnalytics: 'MM-Minute-Styles-Edit',
                label: i18n.t('TEMPLATE_OPTION_EDIT_LABEL'),
                icon: <AtlasIcon name="edit" size="24" />
            },
            {
                id: 1,
                value: MENU_OPTION_PREFERRED,
                testid: 'MinutesCardOverflow_Preferred',
                dataAnalytics: 'MM-Minute-Styles-Preferred',
                label: i18n.t('WRITING_STYLE_PREFERRED'),
                icon: <DefaultStarIcon />
            },
            {
                id: 2,
                value: MENU_OPTION_REGENERATE,
                testid: 'MinutesCardOverflow_Regenerate',
                dataAnalytics: 'MM-Minute-Styles-Regenerate',
                label: i18n.t('WRITING_STYLE_REGENERATE_BUTTON'),
                icon: <RegenerateIcon />
            },
            {
                id: 3,
                value: MENU_OPTION_DELETE,
                testid: 'MinutesCardOverflow_Delete',
                dataAnalytics: 'MM-Minute-Styles-Delete',
                label: i18n.t('DELETE'),
                icon: <TrashIconRed />
            }
        ],
        []
    )
    return (
        <MuiThemeProvider theme={materialAtlasTheme}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
                <PageLayout>
                    <PageHeader
                        title={i18n.t('PAGE_TITLE_MM')}
                        onSignout={authenticationManager.handleSignout}>
                        <MinutesManagerToolbar />
                    </PageHeader>
                    <Box
                        className={classes.header}
                        data-testid="WritingStyles_header"
                        role="banner">
                        <Box>
                            <Typography
                                variant="h1"
                                component="h1"
                                className={classes.subHeaderText}
                                id="writing-styles-title">
                                {i18n.t('YOUR_AI_WRITING_STYLES')}
                            </Typography>
                            <Typography
                                variant="h6"
                                className={classes.subTitleText}
                                aria-describedby="writing-styles-title">
                                {i18n.t('ASSIGN_WRITING_STYLES_TO_COMMITTEES')}
                            </Typography>
                        </Box>
                    </Box>
                    <LinearIndeterminate
                        loading={(isLoading && writingStyles.length === 0) || isLoading}
                    />
                    <Box style={{ marginTop: '24px' }}>
                        <PageBody>
                            <Box className={classes.rootContainer}>
                                {writingStyles.map((writingStyle, _id) => (
                                    <React.Fragment key={writingStyle.id}>
                                        {/* Render the writing style row */}
                                        <Box
                                            className={classes.writingStyleContainer}
                                            onClick={
                                                writingStyle.id !== HARDCODED_DEFAULT_STYLE_ID
                                                    ? () => navigateToStyleDetail(writingStyle)
                                                    : undefined
                                            }
                                            style={
                                                writingStyle.id !== HARDCODED_DEFAULT_STYLE_ID
                                                    ? deletingStyleId === writingStyle.id
                                                        ? {
                                                              cursor: 'not-allowed',
                                                              opacity: 0.5,
                                                              pointerEvents: 'none'
                                                          }
                                                        : { cursor: 'pointer' }
                                                    : undefined
                                            }
                                            data-analytics={
                                                writingStyle.id !== HARDCODED_DEFAULT_STYLE_ID
                                                    ? 'MM-WritingStyles-OpenDetail-StylelistPage'
                                                    : undefined
                                            }>
                                            <Box className={classes.boxContainer}>
                                                <Box className={classes.styleIconAlignment}>
                                                    {writingStyle.id ===
                                                    HARDCODED_DEFAULT_STYLE_ID ? (
                                                        <DiligentBrandIcon />
                                                    ) : (
                                                        <>
                                                            {AIstylesgenerationstatus ===
                                                                WRITING_STYLE_STATUS.PROGRESS &&
                                                            writingStyle.id !==
                                                                HARDCODED_DEFAULT_STYLE_ID ? (
                                                                <div
                                                                    style={{
                                                                        marginRight: '20px'
                                                                    }}>
                                                                    <AILabelNewIcon
                                                                        style={{
                                                                            width: '44px',
                                                                            height: '44px'
                                                                        }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <AIGeneratedNewIcon />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                                <Box className={classes.defaultStyleBox}>
                                                    <Box className={classes.titleContainer}>
                                                        <Typography
                                                            className={classes.defaultStyle}>
                                                            {writingStyle.id ===
                                                            HARDCODED_DEFAULT_STYLE_ID
                                                                ? i18n.t('DILIGENT_AI_STYLE')
                                                                : AIstylesgenerationstatus ===
                                                                      WRITING_STYLE_STATUS.PROGRESS &&
                                                                  writingStyle.id !==
                                                                      HARDCODED_DEFAULT_STYLE_ID
                                                                ? i18n.t('REGENERATING_STYLE_TEXT')
                                                                : i18n.t('CUSTOMISED_AI_STYLE')}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        className={classes.defaultStyleDescription}>
                                                        {writingStyle.id ===
                                                        HARDCODED_DEFAULT_STYLE_ID
                                                            ? i18n.t(
                                                                  'DILIGENT_AI_STYLE_DESCRIPTION'
                                                              )
                                                            : writingStyle.description
                                                            ? i18n.t(
                                                                  'CUSTOMISED_AI_STYLE_DESCRIPTION'
                                                              )
                                                            : ''}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {writingStyle.attributes?.preferred && (
                                                <Box className={classes.preferredTagContainer}>
                                                    <Box className={classes.defaultTag}>
                                                        {i18n.t(
                                                            'MINUTES_TEMPLATES_DEFAULT_TEMPLATE'
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}
                                            <Box
                                                className={classes.actionsContainer}
                                                onClick={(e) => e.stopPropagation()}>
                                                <PopoverButton>
                                                    {({
                                                        onShowPopover,
                                                        isOpen,
                                                        anchorElement,
                                                        onClosePopover
                                                    }) => (
                                                        <>
                                                            <AtlasTooltip
                                                                title={
                                                                    i18n.t(
                                                                        'TEMPLATE_OPTION_MORE_LABEL'
                                                                    ) as string
                                                                }
                                                                interactive
                                                                placement="bottom">
                                                                <span>
                                                                    <AtlasButton
                                                                        data-testid="MinutesManager_Overflow_Btn"
                                                                        data-analytics="MM-WritingStyles-Options"
                                                                        tabIndex={0}
                                                                        className="label-small-link"
                                                                        disableFocusRipple
                                                                        disableRipple
                                                                        onClick={onShowPopover}>
                                                                        <AtlasIcon
                                                                            name="more"
                                                                            size="24"
                                                                        />
                                                                    </AtlasButton>
                                                                </span>
                                                            </AtlasTooltip>
                                                            <Menu
                                                                open={isOpen}
                                                                anchorEl={anchorElement}
                                                                getContentAnchorEl={null}
                                                                anchorOrigin={{
                                                                    vertical: 'bottom',
                                                                    horizontal: 'right'
                                                                }}
                                                                transformOrigin={{
                                                                    vertical: 'top',
                                                                    horizontal: 'right'
                                                                }}
                                                                onClose={onClosePopover}>
                                                                {MENU_OPTIONS.map((option) =>
                                                                    renderMenuItem(
                                                                        option,
                                                                        writingStyle,
                                                                        onClosePopover
                                                                    )
                                                                )}
                                                            </Menu>
                                                        </>
                                                    )}
                                                </PopoverButton>
                                            </Box>
                                        </Box>
                                        {/* Show placeholder row after the default style (first row) */}
                                        {writingStyle.id === HARDCODED_DEFAULT_STYLE_ID &&
                                            !hasCustomStyle && (
                                                <Box className={classes.writingStyleContainer}>
                                                    <Box className={classes.boxContainer}>
                                                        <Box className={classes.styleIconAlignment}>
                                                            {AIstylesgenerationstatus ===
                                                            WRITING_STYLE_STATUS.PROGRESS ? (
                                                                <div
                                                                    style={{ marginRight: '20px' }}>
                                                                    <AILabelNewIcon
                                                                        style={{
                                                                            width: '44px',
                                                                            height: '44px'
                                                                        }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <AIGeneratedNewIcon />
                                                            )}
                                                        </Box>
                                                        <Box className={classes.defaultStyleBox}>
                                                            <Box className={classes.titleContainer}>
                                                                <Typography
                                                                    className={
                                                                        classes.defaultStyle
                                                                    }>
                                                                    {AIstylesgenerationstatus ===
                                                                    WRITING_STYLE_STATUS.PROGRESS
                                                                        ? i18n.t(
                                                                              'GENERATING_CUSTOMISED_AI_STYLE'
                                                                          )
                                                                        : i18n.t(
                                                                              'CUSTOMISED_AI_STYLE'
                                                                          )}
                                                                </Typography>
                                                            </Box>
                                                            <Typography
                                                                className={
                                                                    classes.defaultStyleDescription
                                                                }>
                                                                {i18n.t(
                                                                    'CUSTOMISED_AI_STYLE_DESCRIPTION'
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    {/* Show Generate style button only when not in progress and not in success state */}
                                                    {AIstylesgenerationstatus !==
                                                        WRITING_STYLE_STATUS.PROGRESS &&
                                                        AIstylesgenerationstatus !==
                                                            WRITING_STYLE_STATUS.SUCCESS && (
                                                            <Box
                                                                className={
                                                                    classes.actionsContainer
                                                                }>
                                                                <AtlasButton
                                                                    data-testid="WritingStyles_Generate_Style_Btn"
                                                                    data-analytics="MM-WritingStyles-GenerateStyle-StylelistPage"
                                                                    className={
                                                                        classes.generateStyleButton
                                                                    }
                                                                    onClick={handleGenerateStyle}
                                                                    disabled={isSubmitting}>
                                                                    {i18n.t(
                                                                        'GENERATE_STYLE_BUTTON'
                                                                    )}
                                                                </AtlasButton>
                                                            </Box>
                                                        )}
                                                </Box>
                                            )}
                                    </React.Fragment>
                                ))}
                            </Box>
                        </PageBody>
                    </Box>
                    <div className="custom-toast-container">
                        {Array.from(new Set(toasts.map((toast) => toast.id))).map((id) => {
                            const toast = toasts.find((t) => t.id === id)
                            // Show toasts for writing styles, transcripts, or pregen processes
                            const shouldShowToast =
                                toast?.view_toast === true &&
                                (toast?.forWritingStyle === true ||
                                    toast?.forTranscript === true ||
                                    (toast?.forTranscript === false && !toast?.forWritingStyle))
                            return shouldShowToast ? (
                                <StatusToast key={toast.id} {...toast} removeToast={removeToast} />
                            ) : null
                        })}
                    </div>
                    {showDeleteConfirmation && (
                        <ConfirmationDialog
                            title={i18n.t('WRITING_STYLE_DELETE_TITLE')}
                            message={i18n.t('WRITING_STYLE_DELETE_MESSAGE')}
                            onConfirm={handleDeleteConfirm}
                            onCancel={() => {
                                setShowDeleteConfirmation(false)
                                setStyleToDelete(null)
                            }}
                            confirmLabel={i18n.t('DELETE') as string}
                        />
                    )}
                    {showRegenerateConfirmation && (
                        <ConfirmationDialogGeneric
                            title={i18n.t('WRITING_STYLE_REGENERATE_TITLE')}
                            message={i18n.t('WRITING_STYLE_REGENERATE_MESSAGE_DESCRIPTION')}
                            confirmLabel={i18n.t('WRITING_STYLE_REGENERATE_BUTTON')}
                            onConfirm={handleRegenerateConfirm}
                            onCancel={() => {
                                setShowRegenerateConfirmation(false)
                                setStyleToRegenerate(null)
                            }}
                            confirmAnalytics="MM-WritingStyles-RegenerateConfirm-StylelistPage"
                            cancelAnalytics="MM-WritingStyles-RegenerateCancel-StylelistPage"
                            closeAnalytics="MM-WritingStyles-RegenerateClose-StylelistPage"
                        />
                    )}
                </PageLayout>
            </MuiPickersUtilsProvider>
        </MuiThemeProvider>
    )
}
export default WritingStylesPage
